import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Companies
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  create: (data) => api.post('/companies', data),
  delete: (id) => api.delete(`/companies/${id}`),
};

// Sedes
export const sedesAPI = {
  getAll: (companyId) => api.get('/sedes', { params: { company_id: companyId } }),
  create: (data) => api.post('/sedes', data),
  delete: (id) => api.delete(`/sedes/${id}`),
};

// Floors
export const floorsAPI = {
  getAll: (sedeId) => api.get('/floors', { params: { sede_id: sedeId } }),
  create: (data) => api.post('/floors', data),
  delete: (id) => api.delete(`/floors/${id}`),
};

// Zones
export const zonesAPI = {
  getAll: (floorId) => api.get('/zones', { params: { floor_id: floorId } }),
  create: (data) => api.post('/zones', data),
  delete: (id) => api.delete(`/zones/${id}`),
};

// Spaces
export const spacesAPI = {
  getBySede: (sedeId) => api.get('/spaces/by-sede', { params: { sede_id: sedeId } }),
  getAll: (zoneId) => api.get('/spaces', { params: { zone_id: zoneId } }),
  create: (data) => api.post('/spaces', data),
  delete: (id) => api.delete(`/spaces/${id}`),
};

// Areas
export const areasAPI = {
  getAll: (companyId) => api.get('/areas', { params: { company_id: companyId } }),
  create: (data) => api.post('/areas', data),
  delete: (id) => api.delete(`/areas/${id}`),
};

// Teams
export const teamsAPI = {
  getAll: (areaId) => api.get('/teams', { params: { area_id: areaId } }),
  create: (data) => api.post('/teams', data),
  delete: (id) => api.delete(`/teams/${id}`),
};

// Cargos
export const cargosAPI = {
  getAll: (companyId) => api.get('/cargos', { params: { company_id: companyId } }),
  create: (data) => api.post('/cargos', data),
  delete: (id) => api.delete(`/cargos/${id}`),
};

// Users
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Daily Capacity
export const capacityAPI = {
  getAll: (sedeId) => api.get('/daily-capacity', { params: { sede_id: sedeId } }),
  create: (data) => api.post('/daily-capacity', data),
};

// Assignment Rules
export const rulesAPI = {
  getAll: (companyId) => api.get('/assignment-rules', { params: { company_id: companyId } }),
  create: (data) => api.post('/assignment-rules', data),
};

// Preferences
export const preferencesAPI = {
  getAll: (weekStartDate) => api.get('/preferences', { params: { week_start_date: weekStartDate } }),
  create: (data) => api.post('/preferences', data),
};

// Scheduler
export const schedulerAPI = {
  execute: (data) => api.post('/scheduler/execute', data),
  save: (data) => api.post('/scheduler/save', data),
};

// Assignments
export const assignmentsAPI = {
  getAll: (params) => api.get('/assignments', { params }),
  getDetailed: (params) => api.get('/assignments/detailed', { params }),
  confirm: (id) => api.put(`/assignments/${id}/confirm`),
  cancel: (id) => api.put(`/assignments/${id}/cancel`),
};

// Change Requests
export const requestsAPI = {
  getAll: (status) => api.get('/change-requests', { params: { status } }),
  createChange: (data) => api.post('/change-requests', data),
  createExchange: (data) => api.post('/exchange-requests', data),
  approve: (id) => api.put(`/change-requests/${id}/approve`),
  reject: (id) => api.put(`/change-requests/${id}/reject`),
};

// Waitlist
export const waitlistAPI = {
  getAll: (params) => api.get('/waitlist', { params }),
  add: (sedeId, date) => api.post(`/waitlist?sede_id=${sedeId}&requested_date=${date}`),
  assign: (id) => api.put(`/waitlist/${id}/assign`),
};

// Notifications
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Dashboard
export const dashboardAPI = {
  getStats: (sedeId) => api.get('/dashboard/stats', { params: { sede_id: sedeId } }),
  getHeatmap: (sedeId, month) => api.get('/dashboard/heatmap', { params: { sede_id: sedeId, month } }),
  getEquityReport: (sedeId, dateFrom, dateTo) => api.get('/dashboard/equity-report', { params: { sede_id: sedeId, date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
};

// Audit
export const auditAPI = {
  getLog: (limit = 100) => api.get('/audit-log', { params: { limit } }),
};

export default api;
